import { resolveFormattedTextString, setTrackingId } from "client/Misc/utils";
import {
	ChangeEvent,
	KeyboardEvent,
	MouseEvent,
	ReactElement,
	Ref,
	useCallback,
	useContext,
	useMemo
} from "react";
import styles from "./Toggle.module.less";
import classNames from "classnames";
import { FormattedTextString } from "client/Misc/commonTypes";
import { UniqueInputIdContext } from "client/Contexts/UniqueInputIdContext";

export type ToggleProps = {
	isDisabled?: boolean;
	value: boolean;
	tooltip?: string;
	onChange(newValue: boolean): void;
	shouldBubbleClick?: boolean;
	shouldEllipseText?: boolean;
	trackingId?: string;
	text?: string | FormattedTextString;
	tabIndex?: number;
	onKeyDown?: (args: KeyboardEvent<HTMLElement>) => void;
	ref?: Ref<HTMLInputElement>;
};

export const Toggle = (props: ToggleProps): ReactElement => {
	const {
		isDisabled = false,
		tooltip,
		value,
		onChange,
		shouldBubbleClick = false,
		shouldEllipseText = false,
		trackingId,
		text,
		tabIndex,
		onKeyDown,
		ref
	} = props;

	const { id } = useContext(UniqueInputIdContext);

	const resolvedText = useMemo(
		(): string | undefined => resolveFormattedTextString(text),
		[text]
	);

	const changeHandler = useCallback(
		(args: ChangeEvent<HTMLInputElement>): void => {
			if (shouldBubbleClick === false) {
				args.stopPropagation();
			}

			onChange(args.target.checked);
		},
		[onChange, shouldBubbleClick]
	);

	const rootClickHandler = useCallback(
		(event: MouseEvent): void => {
			if (shouldBubbleClick === false) {
				event.stopPropagation();
			}
		},
		[shouldBubbleClick]
	);

	return (
		<label
			{...setTrackingId(trackingId)}
			className={classNames(
				styles.root,
				{ [styles.root_disabled]: isDisabled }
			)}
			onClick={rootClickHandler}
			onDoubleClick={rootClickHandler}
			title={resolvedText}
		>
			<input
				ref={ref}
				onKeyDown={onKeyDown}
				tabIndex={tabIndex}
				type="checkbox"
				disabled={isDisabled}
				checked={value}
				onChange={changeHandler}
				id={id}
			/>
			<span
				aria-hidden
				className={styles.handle}
				title={tooltip}
			/>

			{resolvedText && (
				<span
					className={classNames(
						styles.text,
						{ [styles.text_ellipsed]: shouldEllipseText }
					)}
				>
					{resolvedText}
				</span>
			)}
		</label>
	);
};
Toggle.displayName = "Toggle";
